import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Sparkles, CheckCircle2, XCircle, RefreshCw, Loader2, Brain, Trophy } from "lucide-react";

/**
 * QuizModal — visar ett 10-frågors övningsprov genererat av Edit.
 *
 * Props:
 *   eventId: id för eventet (läxa/prov)
 *   eventTitle: titel ("Kemiprov")
 *   onClose: stäng-callback
 */
export function QuizModal({ eventId, eventTitle, onClose, onCompleted }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quiz, setQuiz] = useState(null); // { questions: [...] }
  const [answers, setAnswers] = useState({}); // { qIdx: optionIdx }
  const [submitted, setSubmitted] = useState(false);
  const [source, setSource] = useState(""); // "materials" | "llm"
  const [elapsedSec, setElapsedSec] = useState(0);
  // AbortController per generation så att bakgrundsbegäran avbryts om
  // användaren stänger modalen mitt i en LLM-call (annars fortsätter HTTP-
  // requesten i bakgrunden och spinnern hänger nästa gång man öppnar).
  const abortRef = useRef(null);

  // Drift-räknare under loading — visar t.ex. "Pågår sedan 42s" så användaren
  // ser att något händer. Vid >90s visar vi en explicit "Avbryt och försök igen"-knapp.
  useEffect(() => {
    if (!loading) {
      setElapsedSec(0);
      return;
    }
    const id = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [loading]);

  const cancelGeneration = () => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    setLoading(false);
    setError("Generering avbruten. Tryck 'Försök igen' eller stäng modalen.");
  };

  // Konvertera material-quiz-fråga till QuizModal-format (MCQ med correct_index)
  const normalizeMaterialQuestion = (q) => {
    const type = q.type || "multiple_choice";
    if (type === "true_false") {
      const opts = ["Sant", "Falskt"];
      const correct = String(q.correct_answer || "").toLowerCase().startsWith("s") ? 0 : 1;
      return {
        question: q.question, options: opts, correct_index: correct,
        explanation: q.explanation || "", _source_material: q.source_material,
      };
    }
    if (type === "multiple_choice" && Array.isArray(q.options) && q.options.length >= 2) {
      const correctIdx = q.options.findIndex(
        (o) => String(o).trim().toLowerCase() === String(q.correct_answer || "").trim().toLowerCase()
      );
      return {
        question: q.question, options: q.options,
        correct_index: correctIdx >= 0 ? correctIdx : 0,
        explanation: q.explanation || "", _source_material: q.source_material,
      };
    }
    // short_answer — hoppa över (passar inte i QuizModal-format)
    return null;
  };

  const loadFromMaterials = async () => {
    try {
      const { data } = await api.get(`/materials/event/${encodeURIComponent(eventId)}/quiz-pool`);
      const raw = data?.questions || [];
      const normalized = raw.map(normalizeMaterialQuestion).filter(Boolean);
      if (normalized.length >= 3) {
        // Slumpa pool så vi inte alltid får samma 10 i samma ordning
        const shuffled = [...normalized].sort(() => Math.random() - 0.5);
        return {
          questions: shuffled.slice(0, 10),
          source_files: data.source_files || [],
        };
      }
    } catch (e) {
      console.warn("Material-quiz-pool fel", e);
    }
    return null;
  };

  const generate = async ({ forceLLM = false } = {}) => {
    // Avbryt pågående generation om någon
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_) { /* noop */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");
    setQuiz(null);
    setAnswers({});
    setSubmitted(false);
    setSource("");

    // Steg 0: kolla först om backend har en pool med färdiga frågor.
    // Detta är primära flödet efter första generationen — pool-batch returnerar
    // omedelbart med 10 nya frågor och triggar bakgrunds-fyllning om behövligt
    // så nästa "Nya frågor"-klick är instant.
    if (!forceLLM) {
      try {
        const { data: pool } = await api.get(
          `/events/${eventId}/quiz/pool-next?count=10`,
          { timeout: 10_000, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (!pool?.empty && Array.isArray(pool?.questions) && pool.questions.length > 0) {
          setQuiz({ questions: pool.questions, source: "pool", pool_remaining: pool.remaining });
          setSource("llm");
          setLoading(false);
          // Loggning för debugging: bakgrunds-fyllning?
          if (pool.refill_triggered) {
            console.info(`📦 Pool: ${pool.remaining} kvar, bakgrunds-refill startad`);
          }
          return;
        }
      } catch (_) { /* fall through till cache/material/LLM */ }

      // Steg 0b: fallback — försök hämta från det enklare cache-paketet
      // (för events som ännu inte har en pool).
      try {
        const { data: cached } = await api.get(
          `/events/${eventId}/quiz/cached?max_age_sec=300`,
          { timeout: 10_000, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (cached?.cached && Array.isArray(cached.questions) && cached.questions.length > 0) {
          setQuiz(cached);
          setSource("llm");
          setLoading(false);
          // Trigga bakgrunds-refill så poolen byggs upp till nästa öppning.
          api.post(`/events/${eventId}/quiz/prefetch`).catch(() => {});
          return;
        }
      } catch (_) { /* fall through till material/LLM */ }
    }

    // Steg 1: försök hämta frågor från uppladdat material
    if (!forceLLM) {
      const mat = await loadFromMaterials();
      if (controller.signal.aborted) return;
      if (mat) {
        setQuiz({ questions: mat.questions, source_files: mat.source_files });
        setSource("materials");
        setLoading(false);
        return;
      }
    }

    // Steg 2: fallback — generera via LLM. Quiz-generation gör Claude-tema
    // + OpenAI web-search + post-filter → kan ta 60-120s. K8s ingress-proxyn
    // dödar requesten vid ~60s, men backend fortsätter slutföra. Vi pollar
    // därför cachen om HTTP-requesten failar/dödas, och plockar upp resultatet
    // när det dyker upp där.
    let postSucceeded = false;
    try {
      const { data } = await api.post(
        `/events/${eventId}/quiz`,
        {},
        { timeout: 240_000, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (data?.error) {
        setError(data.error);
        return;
      }
      setQuiz(data);
      setSource("llm");
      postSucceeded = true;
      // Bakgrunds-warming: när första generationen lyckats, fortsätt fylla
      // poolen så användaren INTE behöver vänta vid nästa "Nya frågor"-klick.
      api.post(`/events/${eventId}/quiz/prefetch`).catch(() => {});
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      // Network/timeout/k8s ingress-kill → fortsätt och polla cachen, det är
      // vanligt att backend slutfört men proxyn dödat anslutningen.
      console.warn("Quiz POST misslyckades, pollar cache:", e?.message || e);
    }

    // Cache-polling fallback: om POST inte gav resultat, kolla backend-cachen
    // var 5e sekund i upp till 180s. Backend slutför vanligen inom 90-120s.
    if (!postSucceeded) {
      const startedAt = Date.now();
      while (Date.now() - startedAt < 180_000) {
        if (controller.signal.aborted) return;
        await new Promise((res) => setTimeout(res, 5_000));
        if (controller.signal.aborted) return;
        try {
          const { data: cached } = await api.get(
            `/events/${eventId}/quiz/cached?max_age_sec=600`,
            { timeout: 10_000, signal: controller.signal },
          );
          if (controller.signal.aborted) return;
          if (cached?.cached && Array.isArray(cached.questions) && cached.questions.length > 0) {
            setQuiz(cached);
            setSource("llm");
            setLoading(false);
            // Bakgrunds-warming även här så poolen byggs upp.
            api.post(`/events/${eventId}/quiz/prefetch`).catch(() => {});
            return;
          }
        } catch (_) { /* fortsätt polla */ }
      }
      // Timeout — visa felmeddelande men cachen kan fortfarande dyka upp senare,
      // så användaren kan stänga + öppna modalen för att försöka hämta det.
      setError(
        "Det tog för lång tid att generera frågor. Det kan ändå ha lyckats i bakgrunden — stäng och öppna provet igen för att kontrollera, eller tryck 'Försök igen'.",
      );
    }
    if (!controller.signal.aborted) setLoading(false);
  };

  // Avbryt pågående generation vid unmount så HTTP-request inte hänger kvar.
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_) { /* noop */ }
      }
    };
  }, []);

  useEffect(() => {
    generate();
    // eslint-disable-next-line
  }, [eventId]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const answerCount = Object.keys(answers).length;
  const totalQuestions = quiz?.questions?.length || 0;
  const allAnswered = totalQuestions > 0 && answerCount === totalQuestions;

  const correctCount = submitted && quiz
    ? quiz.questions.reduce((acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0), 0)
    : 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-3 sm:p-6"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
      }}
      onClick={onClose}
      data-testid="quiz-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md sm:max-w-lg rounded-2xl shadow-xl flex flex-col overflow-hidden"
        style={{ maxHeight: "calc(100vh - 48px)" }}
        data-testid="quiz-modal"
      >
        <header className="px-5 pt-4 pb-3 border-b border-[color:var(--border)] bg-white flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EEF2F7] text-[color:var(--jarvis)] flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--jarvis)] flex items-center gap-1.5">
              Övningsprov · Edit
              {source === "materials" && (
                <span
                  className="text-[9px] normal-case font-normal tracking-normal bg-[#F4F8F0] text-[#2C5F2D] px-1.5 py-0.5 rounded-full"
                  title="Frågor från ditt uppladdade material"
                  data-testid="quiz-source-materials"
                >
                  Från ditt material
                </span>
              )}
              {quiz?.themes_used && (
                <span
                  className="text-[9px] normal-case font-normal tracking-normal bg-[#EEF2F7] text-[color:var(--jarvis)] px-1.5 py-0.5 rounded-full"
                  title="Claude byggde tema-briefing, OpenAI sökte på webben"
                  data-testid="quiz-dual-badge"
                >
                  Claude + OpenAI
                </span>
              )}
            </p>
            <h2 className="text-base font-medium leading-snug truncate">{eventTitle || "Övningsprov"}</h2>
            {quiz && !submitted && (
              <p className="text-xs text-[color:var(--text-tertiary)] mt-0.5">
                {answerCount} av {totalQuestions} besvarade
              </p>
            )}
            {submitted && (
              <p className="text-xs mt-0.5 flex items-center gap-1 text-[color:var(--moss)]">
                <Trophy size={11} /> Resultat: {correctCount} / {totalQuestions} rätt
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {source === "materials" && !loading && (
              <button
                onClick={() => generate({ forceLLM: true })}
                className="rounded-lg px-2.5 py-1.5 text-[11px] font-medium bg-[color:var(--jarvis)] text-white hover:opacity-90 flex items-center gap-1.5"
                title="Generera helt nya frågor med Edit (LLM)"
                data-testid="quiz-regenerate-llm"
              >
                <Sparkles size={12} />
                Nya frågor
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)]"
              data-testid="quiz-modal-close"
              aria-label="Stäng"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-4 bg-white">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Brain size={28} className="text-[color:var(--jarvis)] animate-pulse mb-3" />
              <p className="text-sm text-[color:var(--text-secondary)]">
                Edit läser igenom materialet och skapar 10 frågor...
              </p>
              <p className="text-xs text-[color:var(--text-tertiary)] mt-1">
                {elapsedSec === 0 ? "Det tar ca 30 sekunder." :
                  elapsedSec < 60 ? `Pågår sedan ${elapsedSec}s · klart om ca ${Math.max(5, 45 - elapsedSec)}s` :
                  `Pågår sedan ${elapsedSec}s · tar längre än vanligt`}
              </p>
              {elapsedSec >= 60 && (
                <div className="mt-4 flex flex-col items-center gap-2">
                  <p className="text-xs text-[color:var(--text-secondary)] max-w-xs">
                    {elapsedSec >= 120
                      ? "Något verkar gå långsamt — du kan avbryta och försöka igen, eller vänta lite till."
                      : "Tar längre än vanligt. Du kan vänta eller avbryta."}
                  </p>
                  <button
                    onClick={cancelGeneration}
                    className="text-xs font-medium px-3 py-1.5 rounded-full border border-[color:var(--border)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)]"
                    data-testid="quiz-cancel-generation"
                  >
                    Avbryt
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl p-4 bg-[#FEE2E2] border border-[#DC2626]/20" data-testid="quiz-error">
              <p className="text-sm text-[#DC2626]">{error}</p>
              <button
                onClick={generate}
                className="mt-3 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-[#DC2626]/30 text-[#DC2626] hover:bg-[#FEE2E2]"
                data-testid="quiz-retry"
              >
                Försök igen
              </button>
            </div>
          )}

          {!loading && quiz?.questions?.map((q, i) => {
            const userAnswer = answers[i];
            const isCorrect = submitted && userAnswer === q.correct_index;
            const isWrong = submitted && userAnswer !== undefined && userAnswer !== q.correct_index;
            return (
              <div
                key={`q-${i}-${(q.question || '').slice(0,30)}`}
                className={`rounded-xl p-4 border transition-colors ${
                  submitted && isCorrect
                    ? "bg-[#F4F8F0] border-[#2C5F2D]/30"
                    : submitted && isWrong
                    ? "bg-[#FEF2F2] border-[#DC2626]/20"
                    : "bg-white border-[color:var(--border)]"
                }`}
                data-testid={`quiz-q-${i}`}
              >
                <div className="flex items-start gap-2 mb-3">
                  <span className="text-[11px] font-semibold text-[color:var(--text-tertiary)] mt-0.5 flex-shrink-0">
                    {i + 1}.
                  </span>
                  <p className="text-sm font-medium leading-snug flex-1">{q.question}</p>
                  {submitted && isCorrect && <CheckCircle2 size={16} className="text-[#2C5F2D] flex-shrink-0 mt-0.5" />}
                  {submitted && isWrong && <XCircle size={16} className="text-[#DC2626] flex-shrink-0 mt-0.5" />}
                </div>

                <div className="space-y-1.5">
                  {q.options.map((opt, j) => {
                    const selected = userAnswer === j;
                    const isThisCorrect = submitted && j === q.correct_index;
                    const isThisWrong = submitted && selected && j !== q.correct_index;
                    return (
                      <button
                        key={`opt-${i}-${j}-${(opt || '').slice(0,20)}`}
                        type="button"
                        onClick={() => {
                          if (submitted) return;
                          setAnswers((prev) => ({ ...prev, [i]: j }));
                        }}
                        disabled={submitted}
                        className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                          isThisCorrect
                            ? "bg-[#F4F8F0] border-[#2C5F2D] text-[#2C5F2D] font-medium"
                            : isThisWrong
                            ? "bg-[#FEF2F2] border-[#DC2626] text-[#DC2626]"
                            : selected
                            ? "bg-[#EEF2F7] border-[color:var(--jarvis)] text-[color:var(--jarvis)]"
                            : "bg-white border-[color:var(--border)] hover:bg-[color:var(--bg-elevated)]"
                        } ${submitted ? "cursor-default" : "cursor-pointer"}`}
                        data-testid={`quiz-q-${i}-opt-${j}`}
                      >
                        {opt}
                        {isThisCorrect && <CheckCircle2 size={12} className="inline ml-1.5 -mt-0.5" />}
                      </button>
                    );
                  })}
                </div>

                {submitted && (
                  <div
                    className="mt-3 rounded-lg p-3 bg-[#EEF2F7] border border-[color:var(--jarvis)]/15"
                    data-testid={`quiz-q-${i}-explanation`}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--jarvis)] mb-1.5 flex items-center gap-1">
                      <Sparkles size={10} /> Edit förklarar
                    </p>
                    <p className="text-xs leading-relaxed text-[color:var(--text-secondary)]">{q.explanation}</p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Material som användes vid generering — räkna upp så användaren ser
              vilka filer som faktiskt slogs in i prompten + om dedup-filtret
              kickade in. */}
          {!loading && quiz?.material_stats && (() => {
            const ms = quiz.material_stats;
            const uploaded = ms.uploaded_materials || [];
            const pdfs = ms.vklass_pdfs || [];
            const totalSources = (
              (ms.teacher_text ? 1 : 0) +
              uploaded.length +
              (ms.classroom_snippets > 0 ? 1 : 0) +
              pdfs.length
            );
            if (totalSources === 0) return null;
            return (
              <div
                className="rounded-xl p-3 bg-[#F4F8F0] border border-[#D5E5C8]"
                data-testid="quiz-material-stats"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2C5F2D] mb-2 flex items-center gap-1.5">
                  <Sparkles size={10} /> Material som använts ({totalSources})
                </p>
                <ul className="space-y-0.5 text-xs text-[color:var(--text-secondary)]">
                  {ms.teacher_text && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2C5F2D]" />
                      Lärarens beskrivning
                    </li>
                  )}
                  {uploaded.map((m, i) => (
                    <li key={`uploaded-${i}-${m.filename}`} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2C5F2D]" />
                      <span className="truncate">{m.filename}</span>
                      <span className="text-[10px] text-[color:var(--text-tertiary)] flex-shrink-0">
                        {m.has_full_text ? `· ${(m.chars / 1000).toFixed(1)}k tecken` : "· endast sammanfattning"}
                      </span>
                    </li>
                  ))}
                  {pdfs.map((p, i) => (
                    <li key={`pdf-${i}-${p.title}`} className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2C5F2D]" />
                      <span className="truncate">{p.title}</span>
                      <span className="text-[10px] text-[color:var(--text-tertiary)] flex-shrink-0">
                        · {(p.chars / 1000).toFixed(1)}k tecken
                      </span>
                    </li>
                  ))}
                  {ms.classroom_snippets > 0 && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2C5F2D]" />
                      {ms.classroom_snippets} Classroom/Gmail-poster
                    </li>
                  )}
                </ul>
                {(quiz.prev_count > 0 || quiz.dedup_filtered > 0) && (
                  <p className="text-[10px] text-[color:var(--text-tertiary)] mt-2 pt-2 border-t border-[#D5E5C8]">
                    {quiz.prev_count} tidigare frågor undveks
                    {quiz.dedup_filtered > 0 && ` · ${quiz.dedup_filtered} dubbletter filtrerades bort`}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Källor från web-sökning — dedupliceras per hostname så listan inte
              upprepar "riksdagen.se" 3 gånger när Quiz-generatorn råkar besöka
              flera underliggande sidor på samma domän. */}
          {!loading && quiz?.citations?.length > 0 && (() => {
            const byHost = new Map();
            for (const url of quiz.citations) {
              let host = url;
              try { host = new URL(url).hostname.replace(/^www\./, ""); } catch (_) {}
              if (!byHost.has(host)) {
                byHost.set(host, { url, host, count: 0 });
              }
              byHost.get(host).count += 1;
            }
            const sources = Array.from(byHost.values());
            return (
              <div
                className="rounded-xl p-3 bg-[color:var(--bg-soft)] border border-[color:var(--border)]"
                data-testid="quiz-citations"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 flex items-center gap-1.5">
                  <Sparkles size={10} className="text-[color:var(--jarvis)]" /> Källor från web-sökning ({sources.length})
                </p>
                <ul className="space-y-1">
                  {sources.map((s, i) => (
                    <li key={s.host || `cite-${i}`}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[color:var(--jarvis)] hover:underline break-all"
                        data-testid={`quiz-citation-${i}`}
                      >
                        {s.host}
                        {s.count > 1 && (
                          <span className="ml-1 text-[10px] text-[color:var(--text-tertiary)]">
                            ({s.count} sidor)
                          </span>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>

        {!loading && quiz && !error && (
          <footer className="px-5 py-3 border-t border-[color:var(--border)] bg-white flex items-center gap-2">
            {!submitted ? (
              <>
                <button
                  onClick={() => generate({})}
                  className="text-xs font-medium px-3 py-2 rounded-full border border-[color:var(--border)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)] flex items-center gap-1.5"
                  title={quiz?.pool_remaining ? `${quiz.pool_remaining} frågor redan förladdade i poolen` : "Hämtar 10 nya frågor"}
                  data-testid="quiz-new"
                >
                  <RefreshCw size={11} /> Nya frågor
                  {quiz?.pool_remaining > 0 && (
                    <span className="text-[10px] text-[color:var(--text-tertiary)] tabular-nums">
                      ({quiz.pool_remaining}+)
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSubmitted(true);
                    if (onCompleted) {
                      const total = quiz.questions.length;
                      const correct = quiz.questions.reduce(
                        (acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0),
                        0
                      );
                      onCompleted({
                        correct, total, percent: Math.round((correct / total) * 100),
                      });
                    }
                  }}
                  disabled={!allAnswered}
                  className={`flex-1 text-sm font-medium px-4 py-2 rounded-full transition-colors ${
                    allAnswered
                      ? "bg-[color:var(--moss)] text-white hover:bg-[#1F4B23]"
                      : "bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)] cursor-not-allowed"
                  }`}
                  data-testid="quiz-submit"
                >
                  {allAnswered ? "Lämna in" : `Svara på alla ${totalQuestions} frågor`}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => generate({})}
                  className="flex-1 text-sm font-medium px-4 py-2 rounded-full bg-[color:var(--jarvis)] text-white hover:opacity-90 flex items-center justify-center gap-1.5"
                  data-testid="quiz-new-after-submit"
                >
                  <RefreshCw size={12} /> Nytt prov
                  {quiz?.pool_remaining > 0 && (
                    <span className="text-[10px] opacity-75 tabular-nums">
                      ({quiz.pool_remaining}+ redo)
                    </span>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="text-sm font-medium px-4 py-2 rounded-full border border-[color:var(--border)] text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)]"
                  data-testid="quiz-close-after-submit"
                >
                  Stäng
                </button>
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

export default QuizModal;
